import React, { ChangeEvent, SetStateAction, useEffect, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import type { Dispatch } from "react"
import Chip from "@mui/material/Chip"
import { TextField } from "@mui/material"
import {
  ContentContainer,
  AmountsContainer,
  ContinueButton,
  ToolButton,
} from "./styled"
import {
  setAmount as setAmountToStore,
  setPaymentMessage,
} from "../../../store/slices/m2mSlice"
import { RootState } from "../../../store/store"
import AmountInput from "../../../components/AmountInput"
import { AmountContainer } from "../../../components/AmountContainer"
import { setNextStep } from "../../../store/slices/routingSlice/routingSlice"
import { PAYMENT_TOOLS_CONFIG } from "./constans"
import { PaymentInfoBlock } from "../../../components/PaymentInfoBlock"
import UIIcon from "../../../components/UIIcon"
import BankIcon from "../../../components/BankIcon"
import { useGetDefaultPaymentToolBank } from "../../../api/hooks/useGetDefaultPaymentToolBank"
import { useCreateSbpOutcomeTtransferRequest } from "../../../api/hooks/useCreateSbpOutcomeTtransferRequest"
import { ICreateSbpOutcomeTransferRequestRequest } from "../../../api/interface"
import {
  clearInputAmount,
  formatCurrency,
  validateAmount,
} from "../../../components/AmountInput/utils/utils"
import { RUBLE } from "../../../utils/constants"
import { STEPS_NAME } from "../../../store/slices/routingSlice/flowsSteps"
import ErrorHandlerModal from "../../../components/Modal/ErrorHandlerModal"
import { useGetUserInfo } from "../../../api/hooks/useGetUserInfo"

const CreateSbpOutcomeTransferRequest = ({
  setOutcomeTransferRequestId,
}: {
  setOutcomeTransferRequestId: Dispatch<SetStateAction<string>>
}) => {
  const dispatch = useDispatch()

  const { data: userInfo } = useGetUserInfo()

  const [availableRefill, setAvailableRefill] = useState(
    userInfo?.availableRefill || 0,
  )

  const paymentMessage = useSelector(
    (state: RootState) => state.refillM2M.paymentMessage,
  )
  const amount = useSelector((state: RootState) => state.refillM2M.amount)
  const selectedBank = useSelector(
    (state: RootState) => state.refillM2M.selectedBank,
  )
  const { mutate: createTransferRequest, data: outcomeTransferRequestResponse, isError } = useCreateSbpOutcomeTtransferRequest()
  const { data: defaultPaymentToolBank } = useGetDefaultPaymentToolBank()

  const [openModal, setOpenModal] = useState(isError)

  const bank = selectedBank || defaultPaymentToolBank
  const handleMessageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = event.target.value
    dispatch(setPaymentMessage(inputValue))
  }

  const handleDoneClick = () => {
    const { bankId } = bank!
    const reqPayload: ICreateSbpOutcomeTransferRequestRequest = {
      bankId,
      amount: Number(amount.replace(",", ".")),
      description: paymentMessage,
    }
    createTransferRequest(reqPayload)
  }
  const handleSubmitClick = () => {
    dispatch(setAmountToStore(clearInputAmount(amount)))
    dispatch(setNextStep(STEPS_NAME.m2mPull.linkingBankSelection))
  }

  const handleChangeButtonClick = () => {
    if (defaultPaymentToolBank) {
      dispatch(setNextStep(STEPS_NAME.m2mPull.paymentToolsM2M))
    } else {
      dispatch(setNextStep(STEPS_NAME.m2mPull.linkingBankSelection))
    }
  }

  const setInputAmount = (
    evt: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const { value } = evt.target
    const formattedValue = formatCurrency(value)
    dispatch(setAmountToStore(clearInputAmount(formattedValue)))
  }

  const buttonDisabled =
    !amount || validateAmount(amount, availableRefill).result
  const isBank = defaultPaymentToolBank || selectedBank

  useEffect(() => {
    if (userInfo?.availableRefill || userInfo?.availableRefill === 0)
      setAvailableRefill(userInfo?.availableRefill)
  }, [userInfo?.availableRefill])

  useEffect(() => {
    if (outcomeTransferRequestResponse) {
      setOutcomeTransferRequestId(outcomeTransferRequestResponse.outcomeTransferRequestId)
      dispatch(setNextStep(STEPS_NAME.m2mPull.refillRequestDetails))
    }
  }, [outcomeTransferRequestResponse])

  useEffect(() => {
    setOpenModal(isError)
  }, [isError])

  return (
    <ContentContainer>
      <AmountContainer />
      <AmountInput
        limit={availableRefill}
        variant="filled"
        value={formatCurrency(amount)}
        onChange={setInputAmount}
        placeholder="Введите сумму ₽"
        label="Сумма пополнения"
        currency={RUBLE}
        inputProps={{ inputMode: "decimal" }}
        marginTop={30}
      />
      <AmountsContainer>
        <Chip label="500 ₽" onClick={() => dispatch(setAmountToStore("500"))} />
        <Chip
          label="1000 ₽"
          onClick={() => dispatch(setAmountToStore("1000"))}
        />
        <Chip
          label="2000 ₽"
          onClick={() => dispatch(setAmountToStore("2000"))}
        />
        <Chip
          label="5000 ₽"
          onClick={() => dispatch(setAmountToStore("5000"))}
        />
      </AmountsContainer>
      {defaultPaymentToolBank || selectedBank ? (
        <PaymentInfoBlock
          icons={<BankIcon logoUrl={bank?.logoUrl} size={46} />}
          title="Способ пополнения"
          onClick={bank ? handleChangeButtonClick : undefined}
          description={{
            main: bank?.title,
            secondary: "Система быстрых платежей",
          }}
        />
      ) : (
        <PaymentInfoBlock
          icons={<UIIcon iconName={PAYMENT_TOOLS_CONFIG.sbp.icon} size={46} />}
          title="Способ пополнения"
          description={{
            main: PAYMENT_TOOLS_CONFIG.sbp.title,
            secondary: PAYMENT_TOOLS_CONFIG.sbp.subtitle,
          }}
        />
      )}

      {isBank && (
        <TextField
          fullWidth
          placeholder="Сообщение"
          sx={{ marginBottom: "18px" }}
          onChange={handleMessageChange}
          value={paymentMessage}
        />
      )}

      {isBank ? (
        <ContinueButton
          variant="contained"
          color="info"
          fullWidth
          onClick={handleDoneClick}
          disabled={buttonDisabled}
        >
          <ToolButton>
            Пополнить через <UIIcon iconName="sbpIcon" size={46} /> сбп
          </ToolButton>
        </ContinueButton>
      ) : (
        <ContinueButton
          variant="contained"
          fullWidth
          onClick={handleSubmitClick}
          disabled={buttonDisabled}
        >
          Продолжить
        </ContinueButton>
      )}
      <ErrorHandlerModal
        open={openModal}
        toggleMOdal={() => {
          setOpenModal(false)
        }}
      />
    </ContentContainer>
  )
}

export default CreateSbpOutcomeTransferRequest
